import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  MeasurementValue, 
  FlowValue, 
  PowerValue, 
  EnergyValue,
  ElectricalEnergyValue,
  TemperatureValue,
  PressureValue,
  HeadValue
} from "@/components/ui/measurement-value";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export function MeasurementsExample() {
  const { t, i18n } = useTranslation();
  const [showUnitNames, setShowUnitNames] = React.useState(false);
  const [unitSystem, setUnitSystem] = React.useState<"metric" | "imperial" | undefined>(undefined);
  
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">{t("examples.measurementUnits")}</h2>
      
      <div className="flex flex-col md:flex-row gap-4">
        <Card className="flex-1">
          <CardHeader>
            <CardTitle>{t("examples.unitOptions")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-md font-semibold mb-2">{t("examples.displayOptions")}</h3>
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  id="show-unit-names" 
                  checked={showUnitNames}
                  onChange={(e) => setShowUnitNames(e.target.checked)}
                />
                <Label htmlFor="show-unit-names">{t("examples.showUnitNames")}</Label>
              </div>
            </div>
            
            <div>
              <h3 className="text-md font-semibold mb-2">{t("examples.unitSystem")}</h3>
              <RadioGroup 
                defaultValue={unitSystem} 
                onValueChange={(value) => setUnitSystem(value === "default" ? undefined : value as "metric" | "imperial")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="default" id="default" />
                  <Label htmlFor="default">{t("examples.automatic")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="metric" id="metric" />
                  <Label htmlFor="metric">{t("units.system.metric")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="imperial" id="imperial" />
                  <Label htmlFor="imperial">{t("units.system.imperial")}</Label>
                </div>
              </RadioGroup>
            </div>
          </CardContent>
        </Card>
        
        <Card className="flex-[2]">
          <CardHeader>
            <CardTitle>{t("examples.unitDisplay")}</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">{t("examples.measurement")}</th>
                  <th className="text-left py-2">{t("examples.value")}</th>
                  <th className="text-left py-2">{t("examples.unit")}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2">{t("examples.flow")}</td>
                  <td className="py-2">500</td>
                  <td className="py-2">
                    <FlowValue 
                      value={500} 
                      unit="GPM" 
                      showUnitName={showUnitNames}
                      forceSystem={unitSystem}
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">{t("examples.power")}</td>
                  <td className="py-2">75</td>
                  <td className="py-2">
                    <PowerValue 
                      value={75} 
                      unit="hp" 
                      showUnitName={showUnitNames}
                      forceSystem={unitSystem}
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">{t("examples.electricalEnergy") || "Electrical Energy"}</td>
                  <td className="py-2">45000</td>
                  <td className="py-2">
                    <ElectricalEnergyValue 
                      value={45000} 
                      unit="kWh" 
                      showUnitName={showUnitNames}
                      forceSystem={unitSystem}
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">{t("examples.thermalEnergy") || "Thermal Energy"}</td>
                  <td className="py-2">100000</td>
                  <td className="py-2">
                    <EnergyValue 
                      value={100000} 
                      unit="BTU" 
                      showUnitName={showUnitNames}
                      forceSystem={unitSystem}
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">{t("examples.temperature")}</td>
                  <td className="py-2">22</td>
                  <td className="py-2">
                    <TemperatureValue 
                      value={22} 
                      unit="°C" 
                      showUnitName={showUnitNames}
                      forceSystem={unitSystem}
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">{t("examples.pressure")}</td>
                  <td className="py-2">14.7</td>
                  <td className="py-2">
                    <PressureValue 
                      value={14.7} 
                      unit="psi" 
                      showUnitName={showUnitNames}
                      forceSystem={unitSystem}
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">{t("examples.head")}</td>
                  <td className="py-2">50</td>
                  <td className="py-2">
                    <HeadValue 
                      value={50} 
                      unit="ft" 
                      showUnitName={showUnitNames}
                      forceSystem={unitSystem}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}